// ── DateCheckinModal — Feature G (feature_date_checkin) ──────────────────────
// Lets the user schedule a date with a partner from a conversation, share the
// plan with a trusted friend (chosen among their named raters), and the server
// auto-followups at the scheduled time + 3 hours later.

import { useEffect, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { supabase } from '../utils/supabase';
import { createDateCheckin } from '../utils/featurePackA';

export default function DateCheckinModal({ partner, matchId, onClose }) {
  const { features, currentUser, lang } = useApp();
  const fr = lang === 'fr';
  const enabled = features?.feature_date_checkin === true;

  const [trustedOptions, setTrustedOptions] = useState([]);
  const [trustedId, setTrustedId]           = useState('');
  const [trustedEmail, setTrustedEmail]     = useState('');
  const [dateAt,    setDateAt]              = useState('');
  const [location,  setLocation]            = useState('');
  const [notes,     setNotes]               = useState('');
  const [busy,      setBusy]                = useState(false);
  const [done,      setDone]                = useState(false);
  const [error,     setError]               = useState(null);

  // Load named raters as trusted-friend options
  useEffect(() => {
    if (!enabled || !currentUser?.id || !supabase) return;
    let alive = true;
    supabase.from('friend_ratings')
      .select('rater_id, rater_name')
      .eq('target_id', currentUser.id)
      .not('rater_id', 'is', null)
      .eq('is_anon', false)
      .limit(15)
      .then(({ data }) => {
        if (!alive) return;
        const seen = new Set();
        const opts = (data || []).filter(r => {
          if (!r.rater_id || seen.has(r.rater_id)) return false;
          seen.add(r.rater_id);
          return true;
        });
        setTrustedOptions(opts);
      });
    return () => { alive = false; };
  }, [enabled, currentUser?.id]);

  if (!enabled) return null;

  const handleSubmit = async () => {
    if (!dateAt) { setError(fr ? 'Choisis date et heure.' : 'Pick date and time.'); return; }
    if (!trustedId && !trustedEmail) { setError(fr ? "Choisis un·e ami·e ou indique un email." : 'Choose a friend or enter an email.'); return; }
    setBusy(true);
    setError(null);
    const isoDate = new Date(dateAt).toISOString();
    const { error: err } = await createDateCheckin({
      userId: currentUser.id,
      matchId: matchId || null,
      partnerName: partner?.name || null,
      trustedId: trustedId || null,
      trustedEmail: trustedEmail || null,
      dateAt: isoDate, location, notes,
    });
    setBusy(false);
    if (err) { setError(err); return; }
    setDone(true);
    setTimeout(onClose, 1800);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 9500, background: 'rgba(10,10,10,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      fontFamily: "'Outfit',system-ui,sans-serif",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#161616', border: '1px solid rgba(201,168,76,0.3)',
        borderRadius: 12, padding: 20, maxWidth: 420, width: '100%',
      }}>
        <p style={{ margin: 0, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#C9A84C' }}>
          {fr ? 'Sécurité' : 'Safety'}
        </p>
        <h3 style={{ margin: '6px 0 6px', fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: '#F2E8D0', fontWeight: 400 }}>
          {fr ? `Date check-in${partner?.name ? ` · ${partner.name}` : ''}` : `Date check-in${partner?.name ? ` · ${partner.name}` : ''}`}
        </h3>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: '#888', lineHeight: 1.5 }}>
          {fr
            ? "Un·e ami·e de confiance sera prévenu·e à l'heure du rendez-vous, puis 3h après pour vérifier que tout va bien."
            : "A trusted friend gets notified at the scheduled time, then 3 hours later to make sure you're safe."}
        </p>

        {done ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <div style={{ fontSize: 36, marginBottom: 6 }}>✓</div>
            <p style={{ color: '#4EC994', fontSize: 13, margin: 0 }}>
              {fr ? 'Check-in programmé. Bonne soirée.' : 'Check-in scheduled. Have a good time.'}
            </p>
          </div>
        ) : (
          <>
            {/* Trusted friend */}
            <label style={lblStyle}>{fr ? 'Ami·e de confiance' : 'Trusted friend'}</label>
            {trustedOptions.length > 0 ? (
              <select value={trustedId} onChange={e => setTrustedId(e.target.value)} style={inputStyle}>
                <option value="">{fr ? '— Choisir parmi tes amis qui t\'ont noté —' : '— Pick from your raters —'}</option>
                {trustedOptions.map(o => (
                  <option key={o.rater_id} value={o.rater_id}>{o.rater_name || 'Friend'}</option>
                ))}
              </select>
            ) : (
              <p style={{ fontSize: 11, color: '#888', margin: '0 0 8px' }}>
                {fr ? "(Pas encore d'amis qui t'ont noté — entre un email)" : '(No raters yet — enter an email below)'}
              </p>
            )}
            <input
              type="email" value={trustedEmail} onChange={e => setTrustedEmail(e.target.value)}
              placeholder={fr ? "ou email de l'ami·e" : "or friend's email"}
              style={{ ...inputStyle, marginTop: 6 }}
            />

            {/* Date / time */}
            <label style={lblStyle}>{fr ? 'Date et heure' : 'Date & time'}</label>
            <input type="datetime-local" value={dateAt} onChange={e => setDateAt(e.target.value)} style={inputStyle}/>

            {/* Location */}
            <label style={lblStyle}>{fr ? 'Lieu (optionnel)' : 'Location (optional)'}</label>
            <input type="text" value={location} onChange={e => setLocation(e.target.value)}
              placeholder={fr ? 'Café, restaurant, quartier…' : 'Café, restaurant, neighbourhood…'}
              style={inputStyle}/>

            {/* Notes */}
            <label style={lblStyle}>{fr ? 'Notes (optionnel)' : 'Notes (optional)'}</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder={fr ? 'Tout ce que tu veux que ton ami·e sache…' : 'Anything your friend should know…'}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 50 }}/>

            {error && (
              <p style={{ color: '#E07070', fontSize: 12, marginTop: 8 }}>{error}</p>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={onClose} style={btnGhost}>{fr ? 'Annuler' : 'Cancel'}</button>
              <button onClick={handleSubmit} disabled={busy} style={btnPrimary}>
                {busy ? '…' : (fr ? 'Programmer le check-in' : 'Schedule check-in')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const lblStyle = {
  display: 'block', fontSize: 10, letterSpacing: '0.12em',
  textTransform: 'uppercase', color: '#888', marginTop: 12, marginBottom: 4,
};
const inputStyle = {
  width: '100%', padding: '8px 10px', background: '#0A0A0A',
  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
  color: '#F2E8D0', fontSize: 13, fontFamily: 'inherit', outline: 'none',
  boxSizing: 'border-box',
};
const btnGhost = {
  flex: 1, padding: 11, background: 'transparent',
  border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6,
  color: '#888', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
};
const btnPrimary = {
  flex: 2, padding: 11, background: '#C9A84C', border: 'none', borderRadius: 6,
  color: '#0A0A0A', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
};
